// frontend/admin-portal/src/pages/PurchaseOrders.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Button, Table, Alert } from "@fiberhome/shared-ui";
import { purchaseOrderService } from "../services/purchaseOrderService";
import { supplierService } from "../services/supplierService";
import { useSettings } from "../contexts/SettingsContext";
import PurchaseOrderStats from "../components/procurement/PurchaseOrderStats";
import PurchaseOrderFilters from "../components/procurement/PurchaseOrderFilters";
import PurchaseOrderStatusBadge from "../components/procurement/PurchaseOrderStatusBadge";

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "IQD");

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    supplierId: "",
    status: "",
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const loadRefs = useCallback(async () => {
    try {
      const list = await supplierService.list({ isActive: true, limit: 200 });
      setSuppliers(list.items || list);
    } catch {
      setSuppliers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await purchaseOrderService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRefs();
  }, [loadRefs]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const columns = [
    {
      key: "code",
      label: "PO",
      render: (r) => (
        <div
          onClick={() => navigate(`/purchase-orders/${r.id}`)}
          style={{ cursor: "pointer" }}
        >
          <div style={{ fontWeight: 600 }}>{r.code}</div>
          {r.requisition && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              {r.requisition.code}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "supplier",
      label: "Supplier",
      render: (r) => r.supplier?.name || <span className="text-muted">—</span>,
    },
    {
      key: "totalAmount",
      label: "Total",
      render: (r) =>
        `${r.currency} ${Number(r.totalAmount || 0).toLocaleString()}`,
    },
    {
      key: "expectedAt",
      label: "Expected",
      render: (r) => r.expectedAt || <span className="text-muted">—</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <PurchaseOrderStatusBadge status={r.status} />,
    },
    {
      key: "actions",
      label: "",
      width: 120,
      render: (r) => (
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate(`/purchase-orders/${r.id}`)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Purchase Orders"
        subtitle="Orders sent to suppliers and their receiving status"
        breadcrumbs={["Home", "Procurement", "Purchase Orders"]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/purchase-orders/import")}
            >
              Import CSV
            </Button>
          </div>
        }
      />

      <PurchaseOrderStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <PurchaseOrderFilters
          filters={filters}
          onChange={setFilters}
          suppliers={suppliers}
        />
      </Card>

      <Card
        title={`Purchase Orders (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No purchase orders yet"
        />
      </Card>
    </>
  );
}
